import { baseDomain } from '../components/Authentification/config';

export interface OTATData {
  token: string;
  expiresAt: number;
  origin: string;
}

export interface AutoLoginParams {
  autoLogin: string;
  profileID: string;
  locale: string;
  code_challenge: string;
  state: string;
  realm: string;
  clientId: string;
  oneTimeAuthToken?: string;
  origin: string;
}

export const OTAT_COOKIE_KEY = 'otat';
export const OTAT_TTL_MINUTES = 5;

export function storeOTAT(token: string): void {
  const expiresAt = Date.now() + OTAT_TTL_MINUTES * 60 * 1000;
  const otatData: OTATData = { token, expiresAt, origin: window.location.origin };

  document.cookie = `${OTAT_COOKIE_KEY}=${encodeURIComponent(JSON.stringify(otatData))}; path=/; max-age=${OTAT_TTL_MINUTES * 60}; secure; samesite=lax; Domain=${baseDomain}`;
}

export function getStoredOTAT(): OTATData | null {
  const cookies = document.cookie.split(';');
  const otatCookie = cookies.find((cookie) => cookie.trim().startsWith(`${OTAT_COOKIE_KEY}=`));

  if (!otatCookie) {
    return null;
  }

  try {
    const otatValue = decodeURIComponent(otatCookie.split('=')[1]);
    const otatData: OTATData = JSON.parse(otatValue);

    if (Date.now() > otatData.expiresAt) {
      clearOTAT();

      return null;
    }

    return otatData;
  } catch (error) {
    console.error('Error parsing OTAT cookie:', error);
    clearOTAT();

    return null;
  }
}

export function clearOTAT(): void {
  document.cookie = `${OTAT_COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=lax`;
}

export function extractAutoLoginParams(): AutoLoginParams | null {
  const urlParams = new URLSearchParams(window.location.search);

  // Parameters added by our signinRedirect() call
  const autoLogin = urlParams.get('autoLogin');
  const profileID = urlParams.get('profileID');
  const locale = urlParams.get('locale');

  // Parameters added automatically by OIDC library
  const code_challenge = urlParams.get('code_challenge');
  const state = urlParams.get('state');

  // KC context parameters (optional, can be provided by config fallback)
  const realm = urlParams.get('realm') || '';
  const clientId = urlParams.get('client_id') || '';
  const otatData = getStoredOTAT();
  const oneTimeAuthToken = urlParams.get('otat') || otatData?.token;
  const origin = otatData?.origin;

  if (!autoLogin || autoLogin !== 'true') {
    return null;
  }

  if (!profileID || !locale || !oneTimeAuthToken || !origin) {
    console.warn(
      'Missing required auto-login parameters: profileID or locale or oneTimeAuthToken',
      profileID,
      locale,
      oneTimeAuthToken,
      origin
    );

    return null;
  }

  // Validate that we have OIDC flow parameters (critical for auto-login)
  if (!code_challenge || !state) {
    console.warn('Missing OIDC flow parameters: code_challenge or state');

    return null;
  }

  return {
    autoLogin,
    profileID,
    locale,
    code_challenge,
    state,
    realm,
    clientId,
    oneTimeAuthToken,
    origin,
  };
}
